import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowsClockwise, CreditCard, Link as LinkIcon } from "@phosphor-icons/react";
import { PayaCardForm } from "@/components/portal/PayaCardForm";
import { portalApi, portalMoney } from "@/lib/portalApi";

const btn =
  "flex flex-1 items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-150 disabled:opacity-60 sm:flex-none";
const solid = `${btn} bg-[#c39e6d] text-white hover:bg-[#a9855a]`;
const outline = `${btn} border border-[#c39e6d] text-[#c39e6d] hover:bg-[#c39e6d]/10`;

// Every way this invoice can be paid online, as returned by the backend (`payment_options`).
export const PortalPayOptions = ({ inv, onChanged }) => {
  const [busy, setBusy] = useState("");
  const [showPaya, setShowPaya] = useState(false);
  const [hostedTx, setHostedTx] = useState("");
  const options = inv.payment_options || [];
  if (!options.length) return null;
  const id = inv.id;

  const stripe = async () => {
    setBusy("stripe");
    try {
      const { data } = await portalApi.post(`/invoices/${id}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Card payment is unavailable right now");
      setBusy("");
    }
  };

  const qboRefresh = async () => {
    setBusy("qbo");
    try {
      const { data } = await portalApi.post(`/invoices/${id}/qbo/refresh`);
      if (data.recorded > 0) toast.success(`${portalMoney(data.recorded)} received — thank you`);
      else toast("No new payment found yet — QuickBooks can take a minute");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not check QuickBooks");
    }
    setBusy("");
  };

  const payaHosted = async () => {
    setBusy("paya");
    try {
      const { data } = await portalApi.post(`/invoices/${id}/paya/payform-url`);
      setHostedTx(data.tx);
      window.open(data.url, "_blank", "noopener");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Card form is unavailable right now");
    }
    setBusy("");
  };

  const payaHostedCheck = async () => {
    setBusy("paya-check");
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/paya/status/${hostedTx}`,
      );
      if (data.payment_status === "paid") {
        toast.success("Payment received — thank you");
        setHostedTx("");
        onChanged?.();
      } else toast("Not confirmed yet — finish the payment in the Paya tab, then check again");
    } catch {
      toast.error("Could not check that payment");
    }
    setBusy("");
  };

  return (
    <div className="w-full space-y-3" data-testid="portal-pay-options">
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          if (o.id === "stripe")
            return (
              <button
                key={o.id}
                type="button"
                data-testid="portal-invoice-pay"
                disabled={!!busy}
                onClick={stripe}
                className={solid}
              >
                <CreditCard size={16} weight="bold" />
                {busy === "stripe" ? "Opening…" : `Pay ${portalMoney(inv.balance)} by card`}
              </button>
            );
          if (o.id === "quickbooks" || o.id === "link")
            return (
              <a
                key={o.id}
                href={o.url}
                target="_blank"
                rel="noreferrer"
                data-testid={o.id === "quickbooks" ? "portal-invoice-pay-quickbooks" : "portal-invoice-pay-link"}
                className={solid}
              >
                <LinkIcon size={16} weight="bold" /> {o.label}
              </a>
            );
          if (o.id === "paya")
            return (
              <button
                key={o.id}
                type="button"
                data-testid="portal-invoice-pay-paya"
                disabled={!!busy}
                onClick={() => (o.mode === "hosted" && o.hosted_ready ? payaHosted() : setShowPaya(true))}
                className={options.some((x) => x.id === "stripe") ? outline : solid}
              >
                <CreditCard size={16} weight="bold" />
                {busy === "paya" ? "Opening…" : `${o.label}${o.sandbox ? " (test)" : ""}`}
              </button>
            );
          return null;
        })}
      </div>
      {options.some((o) => o.id === "quickbooks") && (
        <button
          type="button"
          data-testid="portal-invoice-qbo-refresh"
          disabled={!!busy}
          onClick={qboRefresh}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-60"
        >
          <ArrowsClockwise size={13} weight="bold" className={busy === "qbo" ? "animate-spin" : ""} />
          Paid through QuickBooks already? Check now
        </button>
      )}
      {hostedTx && (
        <button
          type="button"
          data-testid="portal-invoice-paya-check"
          disabled={!!busy}
          onClick={payaHostedCheck}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-60"
        >
          <ArrowsClockwise size={13} weight="bold" /> I’ve finished paying — check status
        </button>
      )}
      {showPaya && (
        <PayaCardForm
          invoiceId={id}
          amount={inv.balance}
          sandbox={options.find((o) => o.id === "paya")?.sandbox}
          onClose={() => setShowPaya(false)}
          onPaid={() => {
            setShowPaya(false);
            onChanged?.();
          }}
        />
      )}
    </div>
  );
};
